<template>
  <v-data-table
    @input="updateSelected"
    :value="currentSelected"
    :headers="headers"
    :items="items"
    :hide-actions="hideActions"
    :sort-icon="sortIcon"
    :no-data-text="noDataText"
    item-key="id"
    class="financial-table"
  >
    <template #headerCell="props">
      <v-checkbox
        v-if="props.header.value === 'selected'"
        :value="allSelected"
        primary
        hide-details
        @click.stop="toggleAll"
      >
      </v-checkbox>
      <span> {{ props.header.text }}</span>
    </template>
    <!-- Slot "row" para sobrescrever qualquer linha -->
    <template #items="listProps">
      <tr v-if="showUnifiedSlips(listProps)" :active="listProps.selected">
        <template v-for="(header, idx) in headers">
          <td :class="header.itemClass || 'text-xs-center'">
            <template v-if="header.value === 'selected'">
              <!-- Seleção de parcelas para solicitação -->
              <v-checkbox
                :input-value="listProps.selected"
                :ref="`checkbox-${listProps.index}`"
                @click.stop="toggleRow(listProps)"
                :disabled="
                  !ignorePrevious &&
                  !(
                    listProps.index === lastSelectedIndex + 1 ||
                    listProps.index === lastSelectedIndex
                  )
                "
                v-if="!listProps.item.totalRow"
                hide-details
              ></v-checkbox>
            </template>
            <template v-else-if="header.value === expandColumn">
              <!-- Expandable unified slips column -->
              <v-tooltip right v-if="showUnifiedTooltip(listProps)">
                <!-- Shows a tooltip only on first slip of unified -->
                <template #activator="{ on }">
                  <span v-on="on">
                    <v-btn-toggle v-model="listProps.expanded">
                      <v-btn value="true" icon class="financial--color">
                        <v-icon>bexsicon-info</v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </span>
                </template>
                <span>Parcela Unificada</span>
              </v-tooltip>
            </template>
            <template v-else-if="$scopedSlots[`col-${header.value}`]">
              <!-- Se tiver um slot para esse header, exibe ele -->
              <slot
                :name="`col-${header.value}`"
                v-bind="listProps"
                :header="header"
              ></slot>
            </template>
            <template v-else>
              <!-- Exibição formatada padrão-->
              {{ showValue(listProps.item, header.value, header.formatter) }}
            </template>
          </td>
        </template>
      </tr>
    </template>
    <template #footer v-if="$scopedSlots[`listFooter`]">
      <slot name="listFooter" v-bind="{ currentSelected, selectedTotal }">
      </slot>
    </template>

    <template v-if="slipDetailsHeaders.length" #expand="positionProps">
      <!-- Slot para expandir detalhes -->
      <slot name="expand" v-bind="positionProps">
        <v-card
          v-if="positionProps.item.contractQty >= 1"
          raised
          class="expanded-area"
        >
          <v-data-table
            v-if="!!positionProps.item[expandColumn].contracts"
            :headers="slipDetailsHeaders"
            :items="positionProps.item[expandColumn].contracts"
            hide-actions
            sort-icon="bexsicon-down"
            :pagination.sync="expandColumnPagination"
            item-key="contractId"
            expand
          >
            <template #items="expandProps">
              <td
                @click.stop="expandProps.expanded = !expandProps.expanded"
                v-for="(header, i) in slipDetailsHeaders"
                :key="i"
                :class="header.itemClass || 'text-xs-center'"
              >
                <template v-if="$scopedSlots[`expandcol-${header.value}`]">
                  <slot
                    :name="`expandcol-${header.value}`"
                    v-bind="expandProps"
                    :header="header"
                  ></slot>
                </template>
                <template v-else>
                  {{
                    showValue(expandProps.item, header.value, header.formatter)
                  }}
                </template>
              </td>
            </template>
            <template #expand="contractProps">
              <div>
                <div
                  dense
                  class="w-100"
                  style="width: 100%; display: inline-table"
                >
                  <thead>
                    <tr>
                      <template v-for="cardHeader in headersWithoutExtra">
                        <th
                          v-if="cardHeader.value === expandColumn"
                          class="text-xs-left"
                        >
                          #
                        </th>
                        <th v-else>
                          {{ cardHeader.text }}
                        </th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(slip, slipIdx) in unitFinancialPositions[
                        contractProps.item.contractId
                      ].filter((item) =>
                        contractProps.item.slips.some(
                          (slipPart) =>
                            slipPart.parcelCode == item.parcelCode &&
                            slipPart.serieId == item.serieId
                        )
                      )"
                      :key="slipIdx"
                    >
                      <template v-for="cardHeader in headersWithoutExtra">
                        <td
                          v-if="cardHeader.value === expandColumn"
                          class="text-xs-left"
                        >
                          {{ slipIdx + 1 }}
                        </td>
                        <td
                          v-else
                          :class="cardHeader.itemClass || 'text-xs-center'"
                        >
                          {{
                            showValue(
                              slip,
                              cardHeader.value,
                              cardHeader.formatter,
                              expandColumn
                            )
                          }}
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </div>
              </div>
            </template>
          </v-data-table>
          <slot name="expand-footer" v-bind="positionProps.item[expandColumn]">
          </slot>
        </v-card>
      </slot>
    </template>
  </v-data-table>
</template>

<script>
import ScreenLoading from '@/components/loaders/ScreenLoading';
export default {
  name: 'FinancialTable',
  components: {
    ScreenLoading,
  },
  props: {
    headers: { type: Array, required: true },
    slipDetailsHeaders: { type: Array, default: () => [] },
    partialUnifiedHeaders: { type: Array, default: null },
    items: { type: Array, default: () => [] },
    unitFinancialPositions: { type: Object, required: true },
    mode: { type: String, required: true },
    loading: { type: Boolean, default: false },
    noDataText: { type: String, default: 'Nenhum dado encontrado' },
    hideActions: { type: Boolean, default: true },
    sortIcon: { type: String, default: 'bexsicon-down' },
    expandColumn: {
      type: String,
      default: 'slipsUnion',
    },

    /*  Selection fields  */
    selectable: { type: Boolean, default: false },
    ignorePrevious: {
      type: Boolean,
    },
    totalKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      currentSelected: [],
      lastSelectedIndex: -1,
      pagination: {
        sortBy: 'expirationDate', // campo inicial para ordenação
        descending: false, // true = decrescente, false = crescente
      },
      expandColumnPagination: {
        sortBy: 'contractId', // campo inicial para ordenação
        descending: false, // true = decrescente, false = crescente
      },
      unifiedContracts: false,
      unifiedShowOnlyFirst: false,
    };
  },
  computed: {
    headersWithoutExtra() {
      return (
        this.partialUnifiedHeaders ||
        this.headers.filter((h) => h.value != 'selected')
      );
    },
    allSelected() {
      return this.currentSelected.length === this.items.length;
    },
    selectedTotal() {
      const total = this.currentSelected.reduce((sum, selected) => {
        let currValue =
          !(selected.contractQty >= 2) || this.mode == 'anticipation'
            ? selected[this.totalKey]
            : selected[this.expandColumn].total;
        const value =
          typeof currValue === 'string'
            ? this.parseMoneyComma(currValue)
            : currValue;
        return sum + value;
      }, 0);

      return this.formatMoney(total);
    },
  },
  methods: {
    showValue(item, headerValue, formatter, extraInfo) {
      let columnValue = item[headerValue];
      if (formatter) {
        return formatter(columnValue, item, extraInfo, headerValue);
      } else {
        return columnValue;
      }
    },
    updateSelected(v) {
      this.currentSelected = v;
      this.$emit('updateSelected', v);
    },
    toggleRow(listPropsItem) {
      if (this.ignorePrevious) {
        listPropsItem.selected = !listPropsItem.selected;
      } else {
        listPropsItem.index === this.lastSelectedIndex + 1 ||
        listPropsItem.index === this.lastSelectedIndex
          ? (() => {
              listPropsItem.selected = !listPropsItem.selected;
              this.lastSelectedIndex =
                listPropsItem.index === this.lastSelectedIndex
                  ? listPropsItem.index - 1
                  : listPropsItem.index;
            })()
          : null;
      }
    },
    toggleAll() {
      this.lastSelectedIndex = this.items.length - 1;
      if (this.currentSelected.length === this.items.length) {
        this.currentSelected = [];
        this.lastSelectedIndex = -1;
      } else {
        this.currentSelected = this.items.slice();
      }
      this.$emit('updateSelected', this.currentSelected);
    },
    showUnifiedTooltip(listProps) {
      if (this.unifiedContracts) {
        /* Shows a tooltip when has different contracts */
        return (
          listProps.item.contractQty >= 2 &&
          listProps.item[this.expandColumn].contracts
        );
      } else {
        /* Shows a tooltip when is unified */
        return listProps.item.contractQty;
      }
    },
    showUnifiedSlips(listProps) {
      if (this.unifiedShowOnlyFirst) {
        // Verifica se não é união ou é a parcela principal da união
        return (
          !listProps.item.contractQty ||
          listProps.item[this.expandColumn].contracts != null
        );
      } else {
        return (
          !(listProps.item.contractQty >= 2) ||
          listProps.item[this.expandColumn].contracts
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.expanded-area {
  background: #eafbe4; /* Um verde bem claro de fundo, combinando com #4bb629 */
  border-left: 4px solid #4bb629; /* Destaque forte à esquerda */
  border-radius: 4px;
  margin: 8px 0;
  padding: 16px;
  transition: background 0.3s, border 0.3s;

  /* ======
  * Agrupamento de linha + expandida (CSS moderno com :has())
  * ====== */
  /* Linha 'top' quando logo abaixo vem uma expandida com conteúdo */

  tr:has(+ tr.v-datatable__expand-row) {
    cursor: pointer;
  }
  tr:has(+ tr.v-datatable__expand-row td.v-datatable__expand-col--expanded) {
    border: 1px solid #b3b1b1;
    border-bottom: none;

    .expanded-area-icon .v-icon {
      rotate: 90deg;
    }
  }

  /* Linha expandida */
  tr.v-datatable__expand-row:has(td.v-datatable__expand-col--expanded) {
    border: 1px solid #b3b1b1;
    border-top: none;

    .v-datatable__expand-col--expanded {
      border: none;
    }
  }
}
.v-btn-toggle {
  box-shadow: none !important;
  background-color: inherit !important;

  .v-btn--active {
    &::before {
      background-color: transparent !important;
    }
    &:hover::before {
      background-color: black !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/financial/FinancialTable.vue